(() => {
  const normalize = (value = '') =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  class TiworksCollectionNavigator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.items = [...this.querySelectorAll('[data-tiworks-quick-filter]')];
      this.notice = this.querySelector('[data-tw-nav-notice]');
      this.currentCollectionHandle = this.dataset.currentCollectionHandle || '';
      this.currentCollectionGroup = this.dataset.currentCollectionGroup || '';
      this.rootCollectionUrl = this.dataset.rootCollectionUrl || '/collections/titanium-bolts';

      this.onCollectionRerendered = this.onCollectionRerendered.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onResize = this.onResize.bind(this);

      this.bindTabs();
      this.bindItems();
      document.addEventListener('collection:rerendered', this.onCollectionRerendered);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.syncFromNativeFilters();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      document.removeEventListener('collection:rerendered', this.onCollectionRerendered);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.resizeTimer);
    }

    bindTabs() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          let next = index;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;

          this.tabs[next]?.focus();
          this.activateTab(this.tabs[next]);
        });
      });

      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (selected) this.activateTab(selected, false);
    }

    bindItems() {
      this.items.forEach((item) => {
        item.addEventListener('click', (event) => this.handleItemClick(event, item));
      });
    }

    handleItemClick(event, item) {
      const targetUrl = item.dataset.collectionUrl || '';
      const targetHandle = item.dataset.collectionHandle || '';
      const itemGroup = item.dataset.filterGroup || '';
      const isCurrentBase = Boolean(targetHandle && targetHandle === this.currentCollectionHandle);

      // Items without a Collection target remain ordinary quick filters.
      if (!targetUrl) {
        event.preventDefault();
        this.applyQuickFilter(item);
        return;
      }

      // On the root Titanium Bolts page there is no active base dimension yet:
      // choose the dedicated SEO Collection directly.
      if (!this.currentCollectionGroup) return;

      // Within the active base dimension, switch to a sibling Collection.
      // Clicking the already-active base item clears that dimension back to the root Collection.
      if (itemGroup === this.currentCollectionGroup) {
        event.preventDefault();
        this.navigateToCollection(isCurrentBase ? this.rootCollectionUrl : targetUrl, item);
        return;
      }

      // Across dimensions, keep the current formal Collection as the base and apply
      // the other dimension as a native Shopify filter. This preserves the current UX.
      const input = this.findMatchingInput(item);
      if (input && !input.disabled) {
        event.preventDefault();
        this.applyQuickFilter(item);
      }
      // If Shopify does not expose the cross-dimension filter on this Collection,
      // fall back to the anchor's formal Collection URL.
    }

    navigateToCollection(targetUrl, item) {
      const target = new URL(targetUrl, window.location.origin);
      const current = new URL(window.location.href);
      const params = new URLSearchParams(current.search);

      // Pagination never survives a taxonomy switch.
      params.delete('page');

      // Remove query filters from the dimension that is becoming the Collection base.
      const input = this.findMatchingInput(item);
      if (input) {
        const names = new Set(this.getGroupInputs(input).map((candidate) => candidate.name).filter(Boolean));
        names.forEach((name) => params.delete(name));
      }

      // Keep the alternate-template preview context while the theme is a draft.
      // On the published theme there is normally no ?view= parameter.
      target.search = params.toString();
      window.location.assign(target.toString());
    }

    activateTab(tab, focus = false) {
      if (!tab) return;

      const targetId = tab.getAttribute('aria-controls');
      this.tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== targetId;
      });
      if (focus) tab.focus();

      window.requestAnimationFrame(() => {
        const activeItem = this.querySelector(`#${targetId} .tiworks-collection-nav__item.is-active`);
        this.ensureItemVisible(activeItem, 'auto');
      });
    }

    onCollectionRerendered() {
      this.syncFromNativeFilters();
    }

    onPopState() {
      window.requestAnimationFrame(() => this.syncFromNativeFilters());
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        const grids = [...this.querySelectorAll('.tiworks-collection-nav__grid')];
        if (window.innerWidth > 767) {
          grids.forEach((grid) => {
            grid.scrollLeft = 0;
          });
          return;
        }

        const activeItem = this.querySelector('.tiworks-collection-nav__item.is-active');
        this.ensureItemVisible(activeItem, 'auto');
      }, 120);
    }

    ensureItemVisible(item, behavior = 'smooth') {
      if (!item || window.innerWidth > 767) return;
      const scroller = item.closest('.tiworks-collection-nav__grid');
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      const viewLeft = scroller.scrollLeft;
      const viewRight = viewLeft + scroller.clientWidth;
      if (itemLeft >= viewLeft && itemRight <= viewRight) return;

      const target = itemLeft - (scroller.clientWidth - item.offsetWidth) / 2;
      scroller.scrollTo({ left: Math.max(0, target), behavior });
    }

    getFilterForm() {
      return document.querySelector('#CollectionFiltersForm');
    }

    getInputLabel(input) {
      const label = input.closest('label');
      if (!label) return input.value;

      const namedLabel = label.querySelector(
        '.m-facet--label, .m-facet--color-name, [class*="visually-hidden"]'
      );
      return namedLabel?.textContent?.replace(/\([^)]*\)\s*$/, '').trim() || input.value;
    }

    getWidgetLabel(input) {
      const widget = input.closest('.m-filter--widget');
      const title = widget?.querySelector('.m-filter--widget-title span, .m-filter--widget-title');
      return title?.textContent?.trim() || '';
    }

    findMatchingInput(item) {
      const form = this.getFilterForm();
      if (!form) return null;

      const expectedFilter = normalize(item.dataset.filterLabel);
      const expectedValue = normalize(item.dataset.filterValue || item.dataset.filterLabel);
      const inputs = [...form.querySelectorAll('input[type="checkbox"][name]')];

      const exact = inputs.find((input) => {
        const widgetLabel = normalize(this.getWidgetLabel(input));
        const inputValue = normalize(input.value);
        const inputLabel = normalize(this.getInputLabel(input));
        const filterMatches = !expectedFilter || widgetLabel === expectedFilter;
        const valueMatches = inputValue === expectedValue || inputLabel === expectedValue;
        return filterMatches && valueMatches;
      });

      if (exact) return exact;

      return inputs.find((input) => {
        const inputValue = normalize(input.value);
        const inputLabel = normalize(this.getInputLabel(input));
        return inputValue === expectedValue || inputLabel === expectedValue;
      });
    }

    getGroupInputs(input) {
      const widget = input?.closest('.m-filter--widget');
      if (!widget) return input ? [input] : [];
      return [...widget.querySelectorAll('input[type="checkbox"][name]')];
    }

    applyQuickFilter(item) {
      const input = this.findMatchingInput(item);
      if (!input || input.disabled) {
        this.showNotice();
        return;
      }

      this.hideNotice();
      const groupInputs = this.getGroupInputs(input);
      const checkedInputs = groupInputs.filter((candidate) => candidate.checked);
      const isOnlyActive = input.checked && checkedInputs.length === 1;

      // Top shortcuts are intentionally single-select inside one filter group.
      // Different groups remain combined. The native sidebar still supports full multi-select.
      groupInputs.forEach((candidate) => {
        candidate.checked = false;
      });
      if (!isOnlyActive) input.checked = true;

      this.syncFromNativeFilters();
      this.submitCurrentFilters(input);
    }

    submitCurrentFilters(input) {
      const form = input?.form || this.getFilterForm();
      const filterElement = form?.closest('collection-filters-form');

      if (form && filterElement && typeof filterElement.renderPage === 'function') {
        const searchParams = new URLSearchParams(new FormData(form)).toString();
        filterElement.renderPage(searchParams);
        return;
      }

      input?.dispatchEvent(new Event('input', { bubbles: true }));
    }

    syncFromNativeFilters() {
      let activeItem = null;

      this.items.forEach((item) => {
        const input = this.findMatchingInput(item);
        const collectionIsActive = Boolean(
          item.dataset.collectionHandle && item.dataset.collectionHandle === this.currentCollectionHandle
        );
        const filterIsActive = Boolean(input?.checked);
        const isActive = collectionIsActive || filterIsActive;
        const hasCollectionTarget = Boolean(item.dataset.collectionUrl);
        const collectionCount = Number.parseInt(item.dataset.collectionCount || '', 10);
        const collectionIsEmpty = hasCollectionTarget && Number.isFinite(collectionCount) && collectionCount <= 0;
        const isUnavailable = hasCollectionTarget
          ? collectionIsEmpty
          : !input || (input.disabled && !input.checked);
        const shouldHide = item.dataset.hideWhenEmpty === 'true' && isUnavailable && !isActive;

        item.hidden = shouldHide;
        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-unavailable', isUnavailable);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (collectionIsActive) item.setAttribute('aria-current', 'page');
        else item.removeAttribute('aria-current');

        if ('disabled' in item) item.disabled = isUnavailable;
        if (hasCollectionTarget) item.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');

        const countNode = item.querySelector('[data-tiworks-filter-count]');
        // Collection-target items keep their full Collection count so the numbers do not
        // collapse when another filter is active. Filter-only items remain dynamic.
        if (countNode && !hasCollectionTarget && input) {
          const sourceCount = input.closest('label')?.querySelector('.m-facet--product-count')?.textContent || '';
          const cleanCount = sourceCount.replace(/[()]/g, '').trim();
          if (cleanCount) {
            countNode.textContent = `${cleanCount} products`;
            countNode.hidden = false;
          }
        }

        if (isActive && !activeItem) activeItem = item;
      });

      const activePanel = activeItem?.closest('[role="tabpanel"]');
      const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-controls') === activePanel?.id);
      if (activeTab) this.activateTab(activeTab, false);

      this.syncBaseDimensionSidebar();
      window.requestAnimationFrame(() => this.ensureItemVisible(activeItem, 'auto'));
    }

    syncBaseDimensionSidebar() {
      if (!this.currentCollectionGroup) return;

      const groupItems = this.items.filter(
        (item) => item.dataset.filterGroup === this.currentCollectionGroup && item.dataset.collectionUrl
      );
      if (!groupItems.length) return;

      const filterLabel = normalize(groupItems[0].dataset.filterLabel);
      if (!filterLabel) return;

      const form = this.getFilterForm();
      if (!form) return;

      const widgets = [...form.querySelectorAll('.m-filter--widget')];
      const widget = widgets.find((candidate) => {
        const title = candidate.querySelector('.m-filter--widget-title span, .m-filter--widget-title');
        return normalize(title?.textContent) === filterLabel;
      });
      if (!widget) return;

      const list = widget.querySelector('.m-facets');
      if (!list) return;

      list.innerHTML = '';
      list.dataset.tiworksCollectionDimension = 'true';

      groupItems.forEach((item) => {
        if (item.hidden) return;

        const targetHandle = item.dataset.collectionHandle || '';
        const isActive = targetHandle === this.currentCollectionHandle;
        const countText = item.querySelector('[data-tiworks-filter-count]')?.textContent || '';
        const countMatch = countText.match(/\d+/);
        const href = isActive ? this.rootCollectionUrl : item.dataset.collectionUrl;

        const li = document.createElement('li');
        li.className = 'm-facet--item';

        const link = document.createElement('a');
        link.className = `m-facet--checkbox tiworks-dimension-link${isActive ? ' is-active' : ''}`;
        link.href = href;
        link.dataset.collectionUrl = href;
        link.dataset.filterGroup = item.dataset.filterGroup || '';
        link.dataset.filterLabel = item.dataset.filterLabel || '';
        link.dataset.filterValue = item.dataset.filterValue || '';
        if (isActive) link.setAttribute('aria-current', 'page');

        link.innerHTML = `
          <span class="tiworks-dimension-link__check" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.5" y="0.5" width="17" height="17" rx="2" stroke="currentColor"></rect>
              <path d="M4.875 9.75L7.5 12.375L13.5 6.375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </span>
          <span class="m-facet--label">${this.escapeHtml(item.dataset.filterValue || item.textContent.trim())}</span>
          ${countMatch ? `<span class="m-facet--product-count">(${countMatch[0]})</span>` : ''}
        `;

        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.navigateToCollection(href, item);
        });

        li.appendChild(link);
        list.appendChild(li);
      });
    }

    escapeHtml(value = '') {
      return value.replace(/[&<>'"]/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      })[char]);
    }

    showNotice() {
      if (!this.notice) return;
      this.notice.classList.add('is-visible');
      this.notice.focus({ preventScroll: true });
    }

    hideNotice() {
      this.notice?.classList.remove('is-visible');
    }
  }

  if (!customElements.get('tiworks-collection-navigator')) {
    customElements.define('tiworks-collection-navigator', TiworksCollectionNavigator);
  }
})();
